/**
 * Helper functions for processing inventory usage logs
 */

/**
 * Extracts and formats a user-friendly item name from usage log data
 *
 * @param {Object} log - The usage log entry
 * @param {Array} inventoryItems - Available inventory items to match against
 * @returns {String} A user-friendly item name
 */
export const getItemNameFromLog = (log, inventoryItems = []) => {
  // Check for direct item name in the log
  if (log.itemName && typeof log.itemName === "string") {
    return log.itemName;
  }

  // Check if item data is included as an object
  if (log.item && log.item.name) {
    return log.item.name;
  }

  // Try to match inventoryId with inventory items
  let inventoryIdValue = log.inventoryId;
  if (typeof inventoryIdValue === "object" && inventoryIdValue !== null) {
    // If inventoryId has name property, use it
    if (inventoryIdValue.name) {
      return inventoryIdValue.name;
    }
    // Extract ID from object
    inventoryIdValue =
      inventoryIdValue.id || inventoryIdValue._id || inventoryIdValue;
  }

  // Try to find matching item by ID
  if (inventoryIdValue && inventoryItems && inventoryItems.length > 0) {
    // Try exact ID match
    const exactMatch = inventoryItems.find(
      (i) =>
        (i._id && i._id.toString() === inventoryIdValue.toString()) ||
        (i.id && i.id.toString() === inventoryIdValue.toString())
    );

    if (exactMatch) {
      return exactMatch.name;
    }

    // Try partial ID match
    const partialMatch = inventoryItems.find((i) => {
      const itemId = (i._id || i.id || "").toString();
      return (
        itemId.includes(inventoryIdValue.toString()) ||
        inventoryIdValue.toString().includes(itemId)
      );
    });

    if (partialMatch) {
      return partialMatch.name;
    }
  }

  // Extract name from reason field if possible
  if (log.reason && typeof log.reason === "string") {
    const match = log.reason.match(/for item: ([^,]+)/i);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  // Return a generic item name with ID if available
  return inventoryIdValue
    ? `Item ${inventoryIdValue.toString().substring(0, 8)}`
    : "Unknown Item";
};

/**
 * Extracts and formats a user-friendly user name from usage log data
 *
 * @param {Object} log - The usage log entry
 * @param {Array} users - Array of all user objects (optional)
 * @param {Object} currentUser - Current logged in user for reference (optional)
 * @returns {String} A user-friendly user name
 */
export const getUserNameFromLog = (log, users = [], currentUser = null) => {
  // 1. Try to resolve from users array if userId is present
  if (log.userId) {
    const id =
      typeof log.userId === "object"
        ? log.userId._id || log.userId.id || log.userId.uid
        : log.userId;
    if (users && Array.isArray(users) && users.length > 0) {
      // Try to find user by any ID field - IMPORTANT: match against firebaseUid!
      const found = users.find(
        (u) =>
          u._id === id ||
          u.id === id ||
          u.uid === id ||
          u.firebaseUid === id || // This is the key change - match against firebaseUid
          (u.authId && u.authId === id)
      );

      console.log(
        `Looking up user ${id}: found? ${!!found}`,
        found || "Not found"
      );

      if (found) {
        return (
          found.displayName ||
          found.name ||
          (found.email ? found.email.split("@")[0] : null) ||
          "Unknown User"
        );
      }
    }
  }
  // 2. Fallback to direct userName or userDisplayName
  if (log.userDisplayName) return log.userDisplayName;
  if (log.userName) return log.userName;

  // 3. Check for creator info
  if (log.createdBy) {
    if (typeof log.createdBy === "object") {
      return (
        log.createdBy.displayName ||
        log.createdBy.name ||
        log.createdBy.email ||
        "Unknown User"
      );
    }
    return log.createdBy;
  }

  // 4. Handle userId object
  if (log.userId && typeof log.userId === "object") {
    return (
      log.userId.displayName ||
      log.userId.name ||
      log.userId.email ||
      "Unknown User"
    );
  }

  // 5. If userId is a string, check if it's an email
  if (log.userId && typeof log.userId === "string") {
    if (log.userId.includes("@")) {
      // Return just the username part of email
      return log.userId.split("@")[0];
    }
    // Try to match with current user
    if (currentUser && currentUser.email && log.userId === currentUser.id) {
      return (
        currentUser.displayName ||
        currentUser.name ||
        currentUser.email.split("@")[0] ||
        "You"
      );
    }
    // Fallback to short UID
    return `User (${log.userId.substring(0, 8)})`;
  }

  return "System";
};
